import { z } from 'zod';
import { API_CONFIG } from '../config/api';
import { apiClient } from '../lib/apiClient';
import { logger } from './logger';
import { apiResponseSchema, authUserSchema } from '../lib/schemas';

export type AuthUser = z.infer<typeof authUserSchema>;

const backendUserResponseSchema = apiResponseSchema(
  authUserSchema.nullable()
).extend({
  code: z.string().optional(),
});

export class EmployeeNotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EmployeeNotFoundError';
  }
}

export const getCurrentUser = async (): Promise<AuthUser | null> => {
  try {
    const apiUrl = `${API_CONFIG.apiVersion}/auth/me`;

    try {
      const data = await apiClient.get(apiUrl, {
        validate: backendUserResponseSchema,
        deduplicate: true,
        context: 'getCurrentUser',
      });

      if (!data.success || !data.data) {
        return null;
      }

      return data.data;
    } catch (apiError: any) {
      if (apiError instanceof Error && 'status' in apiError) {
        const status = (apiError as any).status;

        if (status === 401) {
          logger.debug('No authenticated session found (401)');
          return null;
        }

        if (status === 404) {
          logger.debug('Auth endpoint not found (404) - authentication may not be configured');
          return null;
        }

        if (status === 403) {
          const errorData = (apiError as any).data;
          if (errorData?.code === 'EMPLOYEE_NOT_FOUND') {
            throw new EmployeeNotFoundError(
              errorData.message || 'Employee not found in database'
            );
          } else {
            logger.warn(
              '[getCurrentUser] 403 received but code is not EMPLOYEE_NOT_FOUND:',
              errorData?.code
            );
            return null;
          }
        }

        logger.warn(`Auth check failed with status ${status}`);
        return null;
      }

      throw apiError;
    }
  } catch (error) {
    if (error instanceof EmployeeNotFoundError) {
      throw error;
    }
    logger.error('Failed to get current user:', error);
    return null;
  }
};

export const logout = async (): Promise<void> => {
  try {
    const apiUrl = `${API_CONFIG.apiVersion}/auth/logout`;
    await apiClient.post(apiUrl);
  } catch (error) {
    logger.error('Failed to logout:', error);
  }
};
